import React from 'react';
import type {Column as ColumnType, Task} from '../../models/types';
import {ViewMode, ViewModeEnum} from '../../models/view-types';
import {App} from '../../app/AppSingleton';
import {AppSignals} from '../../app/AppSignals';
import TaskAdd from '../tasks/TaskAdd';
import TaskCard from '../tasks/TaskCard';
import InlineEditWidget from '../elements/InlineEditWidget';
import './Column.css';
import {Disposer} from "../../app/Signals";
import {DndSystem} from '../../dnd-system/core/DndSystem';
import type {
	DragData,
	DropPosition,
	DropResult,
	DropZoneConfig,
	DropZoneContext,
	DropZoneStrategy
} from '../../dnd-system/types';
import {Icons} from '../../constants/icons';
import {TaskOrderProcessor} from '../../utils/TaskOrderProcessor';
import {Board} from "../../models/types";

interface ColumnProps {
	column: ColumnType;
	className?: string;
	index: number;
	onDelete: (columnId: string) => void;
	onTaskAdded: () => void;
	onCancel: () => void;
}

interface ColumnState {
	viewMode: ViewMode;
	isCollapsed: boolean;
	originalCollapsedState: boolean | null; // Оригинальное состояние для восстановления
	forceUpdate: number;
	hasScroll: boolean;
	isDragOver: boolean;
	isEditingTitle: boolean;
}

// Константа для localStorage ключа
const COLLAPSED_PREFIX = 'column-collapsed-';

function SyncProgress({ tasks }: { tasks: Task[] }) {
	//TODO: activate it.
	return null;
}

class Column extends React.Component<ColumnProps, ColumnState> {
	private app: App;
	private dndSystem = DndSystem.getInstance();
	private columnRef = React.createRef<HTMLDivElement>();
	private disposer = new Disposer();
	private dropZoneUnregister?: () => void;
	private tasksContainerRef = React.createRef<HTMLDivElement>();
	private editButtonRef = React.createRef<HTMLButtonElement>();
	private taskOrderProcessor = TaskOrderProcessor.getInstance();

	constructor(props: ColumnProps) {
		super(props);
		this.app = App.getInstance();

		const initialBoard = this.getBoard();
		const initialViewMode = this.app.getCurrentViewMode();
		const shouldForceCollapse = initialBoard.isEditing && initialViewMode === ViewModeEnum.List;

		this.state = {
			viewMode: initialViewMode,
			isCollapsed: shouldForceCollapse ? true : false,
			originalCollapsedState: null, // Будет установлено в componentDidMount
			forceUpdate: 0,
			hasScroll: false,
			isDragOver: false,
			isEditingTitle: false,
		};
	}

	componentDidMount(): void {
		const board = this.getBoard()

		// Загружаем состояние collapsed из localStorage
		const collapsedKey = `${COLLAPSED_PREFIX}${this.props.column.id}`;
		const isCollapsed = localStorage.getItem(collapsedKey) === 'true';
		this.setState({ isCollapsed });

		// Подписка на сигнал принудительного раскрытия
		this.disposer.register(AppSignals.expandColumn, (columnId: string) => {
			if (columnId === this.props.column.id && this.state.isCollapsed) {
				this.setState({ isCollapsed: false });
				localStorage.setItem(collapsedKey, 'false');
			}
		});

		// Подписка на сигнал активации добавления задачи
		this.disposer.register(AppSignals.activateTaskAdd,(columnId: string) => {
			if (columnId !== this.props.column.id) return;

			if ( this.expandIfCollapsed() ) {
				setTimeout(() => {
					AppSignals.activateTaskAdd.emit(columnId);
				}, 10);
			}
		});

		// Подписка на изменение режима просмотра
		this.disposer.register(AppSignals.viewModeChanged,(mode: ViewMode) => {
			const shouldForceCollapse = board.isEditing && mode === ViewModeEnum.List;
			
			if (shouldForceCollapse && !this.state.isCollapsed) {
				// Сохраняем текущее состояние как оригинальное
				const collapsedKey = `${COLLAPSED_PREFIX}${this.props.column.id}`;
				const currentCollapsed = localStorage.getItem(collapsedKey) === 'true';
				
				this.setState({ 
					viewMode: mode,
					isCollapsed: true,
					originalCollapsedState: currentCollapsed
				});
			} else if (!board.isEditing && this.state.originalCollapsedState !== null) {
				// Восстанавливаем оригинальное состояние при выходе из редактирования
				this.setState({ 
					viewMode: mode,
					isCollapsed: this.state.originalCollapsedState,
					originalCollapsedState: null
				});
			} else {
				this.setState({ viewMode: mode });
			}
		});
		
		// Подписка на изменение режима редактирования доски
		this.disposer.register(AppSignals.boardUpdated, () => {
			const shouldForceCollapse = board.isEditing && this.state.viewMode === ViewModeEnum.List;
			
			if (shouldForceCollapse && !this.state.isCollapsed) {
				// Сохраняем текущее состояние как оригинальное
				const collapsedKey = `${COLLAPSED_PREFIX}${this.props.column.id}`;
				const currentCollapsed = localStorage.getItem(collapsedKey) === 'true';
				
				this.setState({ 
					isCollapsed: true,
					originalCollapsedState: currentCollapsed
				});
			} else if (!board.isEditing && this.state.originalCollapsedState !== null) {
				// Восстанавливаем оригинальное состояние при выходе из редактирования
				this.setState({ 
					isCollapsed: this.state.originalCollapsedState,
					originalCollapsedState: null
				});
			}
			
			// Обновляем forceUpdate для перерендера
			this.setState(prevState => ({ forceUpdate: prevState.forceUpdate + 1 }));
		});

		// Подписка на обновление доски
		this.disposer.register(AppSignals.boardUpdated, () => {
			this.setState(prevState => ({ forceUpdate: prevState.forceUpdate + 1 }));
		});

		// Подписка на сигнал подсветки и скролла к задаче
		this.disposer.register(AppSignals.highlightAndScrollToTask, (data) => {
			if (data.columnId === this.props.column.id) {
				// Если колонка свернута, разворачиваем её для отображения задачи
				if (this.state.isCollapsed) {
					this.expandIfCollapsed();
				}

				// После обновления DOM и разворачивания колонки, ищем задачу и скроллим к ней
				setTimeout(() => {
					// Ищем элемент задачи по ID
					const taskElement = document.querySelector(`[data-task-id="${data.taskId}"]`);

					if (taskElement) {
						// Скроллим к самой задаче
						taskElement.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
							inline: 'nearest'
						});
					} else {
						// Если элемент задачи не найден, скроллим к колонке
						if (this.columnRef.current) {
							this.columnRef.current.scrollIntoView({
								behavior: 'smooth',
								block: 'nearest'
							});
						}
					}
				}, 150); // Даем время для обновления DOM
			}
		});

		// Регистрируем drop zone когда элемент доступен
		this.registerDropZone();

		(this.columnRef.current as any).onDndClick = (e: React.MouseEvent<HTMLElement>)=>{
			if (e.target !== this.editButtonRef.current) return;
			if (this.state.isEditingTitle) {
				return;
			}
			this.handleStartEditTitle(e);
		}
	}

	private getBoard() :Board{
		const board = App.getInstance().getBoardById(this.props.column.boardId);
		if (!board) {
			throw Error(`Board '${this.props.column.boardId}' not found`);
		}
		return board;
	}

	componentDidUpdate(prevProps: ColumnProps, prevState: ColumnState): void {
		// Проверяем переполнение контейнера задач после обновлений
		if (this.state.viewMode === ViewModeEnum.List && !this.state.isCollapsed) {
			this.checkScrollOverflow();
		}

		// Если изменилась колонка или режим просмотра, перерегистрируем drop zone
		if (
			(prevProps.column.id !== this.props.column.id ||
				prevState.viewMode !== this.state.viewMode) &&
			this.columnRef.current
		) {
			this.unregisterDropZone();
			this.registerDropZone();
		}
	}

	componentWillUnmount(): void {
		this.disposer.dispose();
		this.unregisterDropZone();
	}

	// Проверяет, есть ли переполнение в контейнере задач
	checkScrollOverflow = (): void => {
		requestAnimationFrame(() => {
			const tasksContainer = this.tasksContainerRef.current;
			if (tasksContainer) {
				const hasScroll = tasksContainer.scrollHeight > tasksContainer.clientHeight;
				if (hasScroll !== this.state.hasScroll) {
					this.setState({ hasScroll });
				}
			}
		});
	};

	// Разворачивает колонку если она свернута
	expandIfCollapsed = (): boolean => {
		if (this.state.isCollapsed && this.state.viewMode === ViewModeEnum.List) {
			const collapsedKey = `${COLLAPSED_PREFIX}${this.props.column.id}`;
			this.setState({ isCollapsed: false });
			localStorage.setItem(collapsedKey, 'false');
			return true;
		}
		return false;
	};

	// Начать редактирование названия колонки
	handleStartEditTitle = (e: React.MouseEvent): void => {
		e.stopPropagation();
		e.preventDefault();
		this.setState({ isEditingTitle: true }, () => {
			// Отключаем DnD при начале редактирования
			this.unregisterDropZone();
			AppSignals.columnEditStart.emit(this.props.column.id);
		});
	};

	// Сохранить новое название колонки
	handleSaveTitle = async (newTitle: string): Promise<void> => {
		try {
			this.props.column.title = newTitle;
			await this.app.columnRename(this.props.column.id, newTitle);
			this.handleCancelEditTitle();
		} catch (error) {
			console.error('Error renaming column:', error);
			// Оставляем в режиме редактирования при ошибке
		}
	};

	// Отменить редактирование названия
	handleCancelEditTitle = (): void => {
		this.setState({ isEditingTitle: false }, () => {
			// Восстанавливаем DnD после отмены
			AppSignals.columnEditFinish.emit(this.props.column.id);
			this.registerDropZone();
		});
	};

	// Сохранить при потере фокуса
	handleBlurSaveTitle = async (newTitle: string): Promise<void> => {
		if (newTitle.trim() && newTitle.trim() !== this.props.column.title) {
			await this.handleSaveTitle(newTitle);
		} else {
			this.handleCancelEditTitle();
		}
	};

	// Обработчик клика по заголовку для сворачивания/разворачивания
	handleToggleExpand = (e: React.MouseEvent): void => {
		e.stopPropagation();

		const newCollapsed = !this.state.isCollapsed;
		this.setState({ isCollapsed: newCollapsed });

		// Сохраняем в localStorage
		const collapsedKey = `${COLLAPSED_PREFIX}${this.props.column.id}`;
		localStorage.setItem(collapsedKey, newCollapsed.toString());

		// Если разворачиваем колонку, возможно нужно прокрутить к ней
		if (!newCollapsed && this.state.viewMode === ViewModeEnum.List) {
			setTimeout(() => {
				this.columnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}, 100);
		}
	};

	// Обработчик удаления колонки
	handleDeleteColumn = (e: React.MouseEvent): void => {
		e.stopPropagation(); // Важно: останавливаем всплытие
		this.app.columnDelete(this.props.column.id).catch(console.error);
	};

	private handleTaskAddedInMobileList = (): void => {
		// Уведомляем Board, что задача добавлена
		// Board закроет режим добавления
		if (this.props.onTaskAdded) {
			this.props.onTaskAdded();
		}
	};

	private handleCancelAddInMobileList = (): void => {
		// Уведомляем Board, что добавление отменено
		if (this.props.onCancel) {
			this.props.onCancel();
		}
	};

	private registerDropZone(): void {
		const element = this.columnRef.current;
		if (!element ) {
			return;
		}

		const context: DropZoneContext = {
			element,
			id: this.props.column.id,
			acceptTypes: ['task']
		};

		const strategy: DropZoneStrategy = {
			cssClasses: {
				active: 'column--drag-over'
			},

			calculateDropPosition: (
				x: number,
				y: number,
				context: DropZoneContext,
				dragData: DragData
			): DropPosition | null => {
				const board = this.getBoard();
				// В режиме редактирования доски не принимаем задачи
				if (board.isEditing) {
					return null;
				}
				
				const element = context.element;
				const rect = element.getBoundingClientRect();

				// Проверяем попадание в bounding box
				if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
					return null;
				}

				// Получаем все задачи в колонке
				const taskElements = Array.from(
					element.querySelectorAll('.task-card[data-task-id]')
				) as HTMLElement[];

				// Если колонка пустая
				if (taskElements.length === 0) {
					return {
						type: 'empty-container',
						relativeX: 0.5,
						relativeY: 0.5,
						data: {
							columnId: context.id,
							index: 0,
							insertAt: 'start'
						},
						visual: {
							className: 'drop-indicator--empty',
							style: {
								top: `${rect.top + 20}px`,
								left: `${rect.left + 10}px`,
								width: `${rect.width - 20}px`,
								height: '100px',
								border: '2px dashed #4dabf7',
								borderRadius: '8px'
							}
						}
					};
				}

				// Ищем позицию между задачами
				for (let i = 0; i < taskElements.length; i++) {
					const taskRect = taskElements[i].getBoundingClientRect();
					const middleY = taskRect.top + taskRect.height / 2;

					if (y < middleY) {
						return {
							type: 'between-tasks',
							relativeX: 0.5,
							relativeY: (y - rect.top) / rect.height,
							data: {
								columnId: context.id,
								index: i,
								insertAt: 'before',
								beforeTaskId: taskElements[i].dataset.taskId
							},
							visual: {
								element: taskElements[i],
								className:"task-drop-indicator",
								style: {
									top: `${taskRect.top - 2}px`,
									left: `${taskRect.left}px`,
									width: `${taskRect.width}px`,
									height: '4px',
									backgroundColor: '#4dabf7',
									borderRadius: '2px'
								}
							}
						};
					}
				}

				// Дроп в конце колонки
				const lastTask = taskElements[taskElements.length - 1];
				const lastRect = lastTask.getBoundingClientRect();

				return {
					type: 'end-of-column',
					relativeX: 0.5,
					relativeY: 0.95,
					data: {
						columnId: context.id,
						index: taskElements.length,
						insertAt: 'end'
					},
					visual: {
						element: lastTask,
						style: {
							top: `${lastRect.bottom}px`,
							left: `${lastRect.left}px`,
							width: `${lastRect.width}px`,
							height: '4px',
							backgroundColor: '#4dabf7',
							borderRadius: '2px'
						}
					}
				};
			},

			onDragEnter: (context: DropZoneContext, dragData: DragData, position: DropPosition) => {
				this.setState({ isDragOver: true });
				context.element.classList.add('column--drag-over');
			},

			onDragLeave: (context: DropZoneContext, dragData: DragData) => {
				this.setState({ isDragOver: false });
				context.element.classList.remove('column--drag-over');
			},

			onDrop: (context: DropZoneContext, dragData: DragData, position: DropPosition): DropResult => {
				this.setState({ isDragOver: false });
				context.element.classList.remove('column--drag-over');

				try {
					// Вызов бизнес-логики перемещения задачи
					// В реальном приложении здесь будет вызов AppSingleton.moveTask()
					console.log('Drop task:', dragData.id, 'to column:', context.id, 'at position:', position.data.index);

					// Вызываем метод App для перемещения задачи
					if (this.app.taskMove) {
						this.app.taskMove(
							dragData.id as string,
							context.id,
							position.data.index
						).catch(console.error);
					}

					return {
						success: true,
						data: position.data,
						message: 'Задача успешно перемещена'
					};
				} catch (error) {
					console.error('Drop failed:', error);
					return {
						success: false,
						error: error instanceof Error ? error.message : 'Unknown error',
						revert: true
					};
				}
			}
		};

		const dropZoneConfig: DropZoneConfig = {
			context,
			strategy
		};

		this.dropZoneUnregister = this.dndSystem.registerDropZone(dropZoneConfig);
	}

	private unregisterDropZone(): void {
		if (this.dropZoneUnregister) {
			this.dropZoneUnregister();
			this.dropZoneUnregister = undefined;
		}
	}

	// Получение задач колонки
	getColumnTasks(): Task[] {
		const { column } = this.props;
		const allTasks = this.getBoard().tasks;

		// Получаем отсортированные ID задач из кэша
		const sortedTaskIds = this.taskOrderProcessor.getSortedTaskIds(column.id, allTasks);

		// Получаем мапу задач для быстрого поиска
		const taskMap = this.taskOrderProcessor.getTaskMap(allTasks);

		// Возвращаем задачи в правильном порядке
		return sortedTaskIds.map(taskId => taskMap.get(taskId)!);
	}

	// Получение количества задач в колонке
	getColumnTasksCount(): number {
		const allTasks = this.getBoard().tasks;
		const sortedTaskIds = this.taskOrderProcessor.getSortedTaskIds(this.props.column.id, allTasks);
		return sortedTaskIds.length;
	}

	// Определение классов для контейнера колонки
	getColumnClasses(): string {
		const { viewMode, isCollapsed, isDragOver } = this.state;
		const { className = '' } = this.props;

		let classes = 'column';

		if (viewMode === ViewModeEnum.Columns) {
			classes += ' column--view_columns';
		} else if (viewMode === ViewModeEnum.List) {
			classes += ' column--view_list';
			if (isCollapsed) {
				classes += ' column--collapsed'
			}
		}

		if (isDragOver) {
			classes += ' column--drag-over';
		}

		if (className) {
			classes += ' ' + className;
		}

		return classes;
	}

	// Определение классов для заголовка
	getHeaderClasses(): string {
		let classes = 'column__header';

		if (this.state.viewMode === ViewModeEnum.Columns) {
			classes += ' column__header--view_columns';
		} else if (this.state.viewMode === ViewModeEnum.List) {
			classes += ' column__header--view_mobile-list';
		}

		return classes;
	}

	renderCollapsedView(): React.ReactNode {
		const { column } = this.props;
		const tasks = this.getColumnTasks();
		const taskCount = this.getColumnTasksCount();
		const unsyncedCount = tasks.filter(t => !t.isSynced).length;

		return (
			<div className="column__collapsed-view">
				<button
					onClick={this.handleToggleExpand}
					className="column__toggle-button"
					aria-expanded="false"
					aria-label={`Развернуть колонку ${column.title}`}
					data-column-id={column.id}
				>
					<div className="column__collapsed-content">
						<div className="column__collapsed-count">
							{taskCount}
						</div>
						<div className="column__collapsed-info">
							<div className="column__collapsed-title">{column.title}</div>
							<div className="column__collapsed-status">
								{unsyncedCount > 0 ? `${unsyncedCount} не синхр.` : 'Все синхронизировано'}
							</div>
						</div>
					</div>
					<span className="column__collapsed-arrow">▼</span>
				</button>
			</div>
		);
	}

	renderColumnHeader(): React.ReactNode {
		const { viewMode, isCollapsed, isEditingTitle } = this.state;
		const { column } = this.props;
		const tasks = this.getColumnTasks();
		const board = this.getBoard();

		const headerSpec = {
			tooltip: board.isEditing ? "Перетащите колонку" : ""
		};
		let classHasTasks = tasks.length > 0 ? 'column__task-count--has-tasks' : '';

		// Если редактируем название, показываем виджет редактирования
		if (isEditingTitle && board.isEditing && viewMode !== ViewModeEnum.List) {
			return (
				<div className={this.getHeaderClasses()}>
					<div className="column__header-content">
						<div className="column__header-main column__title-edit-container">
							<InlineEditWidget
								value={column.title}
								onSave={this.handleSaveTitle}
								onEditStart={() => {
									AppSignals.columnEditStart.emit(this.props.column.id);
								}}
								onEditFinish={() => {
									AppSignals.columnEditFinish.emit(this.props.column.id);
								}}
								onCancel={this.handleCancelEditTitle}
								onBlurSave={this.handleBlurSaveTitle}
								placeholder="Название колонки"
								className="column__edit-widget"
								autoFocus={true}
							/>
							<span className={`column__task-count ${classHasTasks}`}
								  aria-label={`Количество задач: ${tasks.length}`}>{tasks.length}</span>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className={this.getHeaderClasses()}
				 onClick={viewMode === ViewModeEnum.List ? this.handleToggleExpand : undefined}
			>
				<div className="column__header-content">
					<div className="column__header-main">
						{/* Заголовок колонки с иконкой drag */}
						<div className="column__title-container">
							<h3 className="column__title" aria-label={`Колонка: ${column.title}`} title={headerSpec.tooltip}>
								{column.title}
							</h3>
							{board.isEditing && viewMode !== ViewModeEnum.List && (
								<button
									ref={this.editButtonRef}
									className="column__edit-button"
									onClick={this.handleStartEditTitle}
									title="Редактировать название"
									aria-label={`Редактировать название колонки ${column.title}`}
									onMouseDown={(e) => e.stopPropagation()}
									draggable={false}
									data-dnd-ignore="true"
								>
									{Icons.Edit}
								</button>
							)}
						</div>
						<span className={`column__task-count ${classHasTasks}`}
							  aria-label={`Количество задач: ${tasks.length}`}>{tasks.length}</span>
					</div>

					<div className="column__header-actions">
						{/*
						  Показываем кнопку удаления только если:
						  - Включен режим редактирования доски
						  - На доске больше одной колонки (нельзя удалить последнюю колонку)
						  - Не в режиме списка (в мобильном виде)
						  - Не в режиме редактирования заголовка
						*/}
						{board.isEditing && board.columns.length > 1 && viewMode !== ViewModeEnum.List && !isEditingTitle && (
							<button
								className="column__delete-button"
								onClick={this.handleDeleteColumn}
								title="Удалить колонку"
								aria-label={`Удалить колонку ${column.title}`}
								onMouseDown={(e) => e.stopPropagation()}
								draggable={false}
							> × </button>
						)}

						{viewMode === ViewModeEnum.List && (
							<button
								onClick={this.handleToggleExpand}
								className="column__expand-button"
								title={isCollapsed ? "Развернуть" : "Свернуть"}
								draggable={false}
							>
								{!isCollapsed ? '▲' : '▼'}
							</button>
						)}
					</div>
				</div>

				{viewMode === ViewModeEnum.List && tasks.length > 0
					&& <SyncProgress tasks={tasks}/>}
			</div>
		);
	}

	renderTasksList(): React.ReactNode {
		const { viewMode } = this.state;
		const tasks = this.getColumnTasks();
		const className = `column__tasks column__tasks--${viewMode} has-scrollbar`;

		return (
			<div
				ref={this.tasksContainerRef}
				className={className}
			>
				{tasks.map((task, index) => (
					<TaskCard key={task.localId} task={task} index={index} />
				))}
			</div>
		);
	}

	render(): React.ReactNode {
		const { viewMode, isCollapsed } = this.state;
		const { column } = this.props;
		const { hasScroll } = this.state;

		// Если это мобильный список и колонка свернута
		let isColumnCollapsed = viewMode === ViewModeEnum.List && isCollapsed;

		let classSticky = viewMode === ViewModeEnum.List && hasScroll ? 'column__add-task-container--sticky' : '';
		let columnContent = <>
			{this.renderTasksList()}
			{/* Кнопка добавления задачи */}
			<div className={`column__add-task-container ${classSticky}`}>
				<TaskAdd
					columnId={column.id}
					boardId={column.boardId}
					onTaskAdded={this.handleTaskAddedInMobileList}
					onCancel={this.handleCancelAddInMobileList}
					autoFocus={true}
				/>
			</div>
		</>;

		return (
			<div
				id={column.id}
				ref={this.columnRef}
				className={this.getColumnClasses()}
				role="list"
				aria-label={`Колонка: ${column.title}`}
			>
				{this.renderColumnHeader()}
				{isColumnCollapsed ? null : columnContent}
			</div>
		);
	}
}

export default Column;